import * as R from 'ramda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { animated, useSpring } from 'react-spring'
import styled, { keyframes } from 'styled-components'

import { sleep } from '../../utils/commonUtils'
import Avatar from '../Avatar'

const DonorAvatars = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 5px;
  @media (max-width: 425px) {
    display: none;
  }
`

const ring = keyframes`
  0%{
    transform: rotateZ(0deg);
  }
  100%{
    transform: rotateZ(360deg)
  }
`
const AvatarRing = styled.span`
  display: block;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
  position: absolute;
  top: -1px;
  left: -1px;
  border-radius: 50%;
  border: 1px dashed #26c699;
  overflow: hidden;
  animation: ${ring} 2.5s linear infinite;
`
const AnimatedAvatar = animated(Avatar)
const AnimatedAvatarRing = animated(AvatarRing)

const DonorAvatar = React.memo(
  ({ hasMoreDonor, shouldAnimate, index, avatarCount, ...avatarProps }) => {
    const ringStyles = useSpring({
      from: { opacity: hasMoreDonor ? 1 : 0 },
      to: !shouldAnimate
        ? {}
        : hasMoreDonor
        ? async (next, cancel) => {
            await sleep(1600)
            await next({
              opacity: 0
            })
          }
        : {}
    })
    const firstAvatarStyles = useSpring({
      from: {
        opacity: 0,
        transform: `translateX(calc(0% - 0px)) scale(${
          hasMoreDonor ? `0,0` : `1,1`
        })`
      },
      to: !shouldAnimate
        ? {
            opacity: 0,
            transform: `translateX(calc(0% - 0px)) scale(${
              hasMoreDonor ? `0,0` : `1,1`
            })`
          }
        : async (next, cancel) => {
            if (hasMoreDonor) {
              await sleep(800)
              await next({
                opacity: 1,
                transform: 'translateX(calc(0% - 0px)) scale(1,1)'
              })
              await sleep(800)
              await next({
                transform: 'translateX(calc(100% - 5px)) scale(1,1)'
              })
            } else {
              await next({
                opacity: 1,
                transform: 'translateX(calc(0% - 0px)) scale(1,1)'
              })
            }
          }
    })
    const otherAvatarsStyles = useSpring({
      from: { opacity: 0, transform: 'translateX(calc(0% - 0px))' },
      to: !shouldAnimate
        ? { opacity: 0, transform: 'translateX(calc(0% - 0px))' }
        : async (next, cancel) => {
            await next({ opacity: 1 })
            if (hasMoreDonor) {
              await next({
                transform:
                  index === avatarCount - 1
                    ? 'translateX(calc(0% - 0px))'
                    : 'translateX(calc(100% - 5px))'
              })
            }
          }
    })
    return (
      <AnimatedAvatar
        css={`
          margin-right: -5px;
          opacity: 0;
          z-index: ${avatarCount - index};
        `}
        style={index === 0 ? firstAvatarStyles : otherAvatarsStyles}
        ring={index === 0 && <AnimatedAvatarRing style={ringStyles} />}
        {...avatarProps}
      />
    )
  }
)

export default React.memo(({ avatars, donorCount, ...props }) => {
  const displayAvatars = useMemo(
    () =>
      R.reverse(
        avatars.slice(0, window.matchMedia('(max-width:767px)').matches ? 6 : 9)
      ),
    [JSON.stringify(avatars)]
  )

  const loadedCount = useRef(0)

  const [imgsLoaded, setImgsLoaded] = useState(false)

  const handleAvatarOnLoad = useCallback(() => {
    loadedCount.current++
    if (loadedCount.current === R.length(displayAvatars)) {
      loadedCount.current = 0
      setImgsLoaded(true)
    }
  }, [])

  return (
    <DonorAvatars {...props}>
      {displayAvatars.map(({ avatar, donorId }, i) => (
        <DonorAvatar
          onLoad={handleAvatarOnLoad}
          shouldAnimate={imgsLoaded}
          key={donorId}
          index={i}
          size={32}
          borderColor='#fff'
          src={avatar}
          avatarCount={displayAvatars.length}
          hasMoreDonor={R.gt(donorCount, 8)}
        />
      ))}
    </DonorAvatars>
  )
})
